import { Injectable } from '@angular/core';
import { AssetListItem, MeasurementUnitsService } from 'app/services';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { AppService } from 'app/app.service';
import { TelemetryStateResponse, AssetStateProfileListResponse } from '@key-telematics/fleet-api-client';
import { TranslateService } from '@ngx-translate/core';
import { MeasurementUnitType } from 'app/services/measurement-units/measurement-units.service';

export interface TelemetryItem {
    key: string; // TODO: remove from interface
    name: string;
    value: string;
    full?: boolean; // TODO: remove from interface
}

export interface SanitizedTelemetry {
    date?: TelemetryItem;
    received?: TelemetryItem;
    active?: TelemetryItem;
    latitude?: TelemetryItem;
    longitude?: TelemetryItem;
    speed?: TelemetryItem;
    heading?: TelemetryItem;
    altitude?: TelemetryItem;
    satellites?: TelemetryItem;
    odometer?: TelemetryItem;
    hours?: TelemetryItem;
    distance_violation?: TelemetryItem;
    active_violation?: TelemetryItem;
    inactive_violation?: TelemetryItem;
}

export type DetailLocation = 'bottom' | 'left' | 'right';

@Injectable()
export class OverviewStateService {

    private selectedAssetSubject = new BehaviorSubject<AssetListItem>(null);
    selectedAsset$: Observable<AssetListItem>;
    selectedAsset: AssetListItem;

    private zoomToSubject = new Subject<{ lat: number, lon: number, zoom?: number }>();
    zoomTo$ = this.zoomToSubject.asObservable();

    private detailLocationSubject = new BehaviorSubject<DetailLocation>('bottom');
    detailLocation$ = this.detailLocationSubject.asObservable();

    currentTab: string;

    
    private assetsShownSubject = new ReplaySubject<string[]>(1);
    assetsShown$ = this.assetsShownSubject.asObservable();

    private assetsHiddenSubject = new ReplaySubject<string[]>(1);
    assetsHidden$ = this.assetsHiddenSubject.asObservable();

    private hiddenMapAssetSubject = new BehaviorSubject<string[]>([]);
    hiddenMapAssets$ = this.hiddenMapAssetSubject.asObservable();

    private sidebarVisibilitySubject = new BehaviorSubject<boolean>(false);
    sidebarVisibility$ = this.sidebarVisibilitySubject.asObservable();

    private cache: { [key: string]: Promise<any> } = {};

    constructor(
        public app: AppService,
        private router: Router,
        private units: MeasurementUnitsService,
        private i18n: TranslateService
    ) {
        this.selectedAsset$ = this.selectedAssetSubject.asObservable();
        this.currentTab = 'activity';
        this.app.client$.subscribe(() => {
            this.cache = {};
            this.setAsset(null); // if the client changes, set the asset to null
        });
    }

    navigateToRoot() {
        this.router.navigate(['/status']);
    }

    navigateToAsset(assetId: string, tab?: string) {
        this.currentTab = tab || this.currentTab;
        const id = assetId || (this.selectedAsset && this.selectedAsset.id);
        if (id) {
            this.router.navigate(['/status', id, this.currentTab]);
        }
    }

    zoomTo(lat: number, lon: number, zoom?: number) {
        this.zoomToSubject.next({ lat: lat, lon: lon, zoom: zoom });
    }

    setAsset(asset: AssetListItem) {
        this.selectedAsset = asset;
        this.selectedAssetSubject.next(this.selectedAsset);
    }

    setDetailLocation(location: DetailLocation) {
        this.detailLocationSubject.next(location);
    }

    hideMapAssets(assetIds: string[]) {
        this.assetsHiddenSubject.next(assetIds);
    }

    showMapAssets(assetIds: string[]) {
        this.assetsShownSubject.next(assetIds);
    }

    setHiddenMapAssets(assetIds: string[]) {
        this.hiddenMapAssetSubject.next(assetIds);
    }

    closeSidebar() {
        this.sidebarVisibilitySubject.next(false);
    }

    openSidebar() {
        this.sidebarVisibilitySubject.next(true);
    }

    getSanitizedTelemetry(telemetry: TelemetryStateResponse, assetId?: string): SanitizedTelemetry {
        if (!telemetry) { return {} as SanitizedTelemetry; }

        const result: SanitizedTelemetry = {
            date: { key: 'date', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.DATE'), value: this.units.format(telemetry.date, 'date').format + ' ' + this.units.format(telemetry.date, 'time').format },
            received: { key: 'received', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.RECEIVED'), value: this.units.format(telemetry.received, 'date').format + ' ' + this.units.format(telemetry.received, 'time').format },
        };

        if (telemetry.location) {
            result['latitude'] = { key: 'latitude', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.LAT'), value: telemetry.location.lat.toString() };
            result['longitude'] = { key: 'longitude', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.LON'), value: telemetry.location.lon.toString() };

            if (telemetry.telemetry?.gps_acc !== undefined) {
                const meters = telemetry.telemetry.gps_acc;
                const value = meters >= 0 ? this.units.format(meters, 'altitude').format : this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.LAST_KNOWN');
                result['accuracy'] = { key: 'accuracy', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.ACCURACY'), value };
            }

            result['speed'] = { key: 'speed', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.SPEED'), value: this.units.format(telemetry.location.speed, 'speed').format };
            result['heading'] = { key: 'heading', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.HEADING'), value: telemetry.location.heading + ' °' };

            if (telemetry.location.altitude >= 0) {
                result['altitude'] = { key: 'altitude', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.ALTITUDE'), value: this.units.format(telemetry.location.altitude, 'altitude').format };
            }
            if (telemetry.location.accuracy >= 1) {
                result['satellites'] = { key: 'satellites', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.SATELLITES'), value: telemetry.location.accuracy.toString() };
            }
        }

        if (telemetry.spd) {
            const { un, rd, md } = telemetry.spd;
            result['speedLimit'] = {
                key: 'speedLimit',
                name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.SPEED_LIMIT'),
                value: this.getClientSpecificSpeedValue(md || rd, un ? 'milesPerHour' : 'kilometresPerHour', 'speed'),
            };
        }

        result['active'] = { key: 'active', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.ACTIVE'), value: telemetry.active ? this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.YES') : this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.NO') };
        result['linked'] = { key: 'linked', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.HEADER.LINKED'), value: telemetry.linked.filter(x => x.id !== assetId).map(x => x.name).join(', ') };

        if (telemetry.counters) {
            if (telemetry.counters['odometer']) {
                result['odometer'] = { key: 'odometer', name: this.i18n.instant(`STATUS.DETAILS.SIDEBAR.TELEMETRY.ODOMETER`), value: this.units.format(telemetry.counters['odometer'], 'distance').format };
            }
            if (telemetry.counters['hours']) {
                result['hours'] = { key: 'hours', name: this.i18n.instant(`STATUS.DETAILS.SIDEBAR.TELEMETRY.HOURS`), value: (Math.round(telemetry.counters['hours'] * 10) / 10).toString() };
            }
        }
        this.sanitizeTripViolations(telemetry, result);
        return result;
    }


    getAssetStateProfiles(ownerId: string): Promise<AssetStateProfileListResponse> {
        let result = this.cache[ownerId];
        if (!result) {
            result = this.app.api.entities.listAssetStateProfiles(ownerId).catch(error => {
                if (error.name === 'ForbiddenError') {
                    return Promise.resolve<AssetStateProfileListResponse>({ items: [], count: 0, limit: 0, offset: 0 });
                }
                throw error;
            });
            this.cache[ownerId] = result;
        }
        return result;
    }

    getClientSpecificSpeedValue(value: number, unit: string, measurement: MeasurementUnitType): string {
        const clientUnit = this.units.getUnitConfig(measurement);
        const symbol = this.units.getUnitList(measurement).units[clientUnit].symbol;

        if (clientUnit === unit) {
            return value + ' ' + symbol;
        } else {
            if (unit === 'milesPerHour') { // in miles, so convert to km
                value = value * 1.609344;
            }
            return this.units.format(value, measurement).format;
        }
    }

    private sanitizeTripViolations({ telemetry }: TelemetryStateResponse, result: SanitizedTelemetry) {
        if (!telemetry) {
            return;
        }
        if (telemetry.distance_violation) {
            result['distance_violation'] = { key: 'distance_violation', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.DISTANCE_VIOLATION'), value: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.YES') };
        }

        if (telemetry.active_violation) {
            result['active_violation'] = { key: 'active_violation', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.ACTIVE_VIOLATION'), value: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.YES') };
        }

        if (telemetry.inactive_violation) {
            result['inactive_violation'] = { key: 'inactive_violation', name: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.INACTIVE_VIOLATION'), value: this.i18n.instant('STATUS.DETAILS.SIDEBAR.TELEMETRY.YES') };
        }
    }

}
